/**
 * polygonUtils.js
 * Utility functions for polygon coordinate handling, validation, and conversion.
 * Used by the No Entry Zone polygon editor.
 */

/**
 * Parse a resolution string like "1920x1080" into { width, height }.
 * Falls back to 1920x1080 if parsing fails.
 *
 * @param {string} resolution - e.g. "1920x1080"
 * @returns {{ width: number, height: number }}
 */
export const parseResolution = (resolution) => {
  if (!resolution || typeof resolution !== "string") {
    return { width: 1920, height: 1080 };
  }
  const parts = resolution.toLowerCase().split("x");
  if (parts.length === 2) {
    const w = parseInt(parts[0], 10);
    const h = parseInt(parts[1], 10);
    if (!isNaN(w) && !isNaN(h) && w > 0 && h > 0) {
      return { width: w, height: h };
    }
  }
  return { width: 1920, height: 1080 };
};

/**
 * Convert polygon from reference coordinate space to normalized [0..1] space.
 * Reference polygon: [[refX, refY], ...]
 * Normalized: [{ x: 0..100, y: 0..100 }, ...]  (percent for SVG)
 *
 * @param {number[][]} polygon - [[refX, refY], ...]
 * @param {number} refWidth
 * @param {number} refHeight
 * @returns {{ x: number, y: number }[]}
 */
export const polygonToPercent = (polygon, refWidth, refHeight) => {
  if (!Array.isArray(polygon) || polygon.length === 0) return [];
  const rw = refWidth > 0 ? refWidth : 1920;
  const rh = refHeight > 0 ? refHeight : 1080;
  return polygon.map(([rx, ry]) => ({
    x: (rx / rw) * 100,
    y: (ry / rh) * 100,
  }));
};

/**
 * Convert polygon from normalized percent space to reference coordinate space.
 * Used before sending to the API.
 *
 * @param {{ x: number, y: number }[]} points - percent space points
 * @param {number} refWidth
 * @param {number} refHeight
 * @returns {number[][]} [[refX, refY], ...]
 */
export const percentToPolygon = (points, refWidth, refHeight) => {
  if (!Array.isArray(points) || points.length === 0) return [];
  const rw = refWidth > 0 ? refWidth : 1920;
  const rh = refHeight > 0 ? refHeight : 1080;
  return points.map((pt) => [
    Math.round((pt.x / 100) * rw),
    Math.round((pt.y / 100) * rh),
  ]);
};

/**
 * Validate polygon points before saving.
 *
 * @param {{ x: number, y: number }[]} points
 * @param {number} requiredCount - the target number of vertices
 * @returns {{ valid: boolean, message: string | null }}
 */
export const validatePolygon = (points, requiredCount) => {
  if (!Array.isArray(points) || points.length === 0) {
    return { valid: false, message: "No polygon points defined. Please draw a polygon first." };
  }

  if (points.length < 3) {
    return { valid: false, message: "A valid polygon requires at least 3 vertices." };
  }

  if (requiredCount && points.length !== requiredCount) {
    return {
      valid: false,
      message: `You have ${points.length} of ${requiredCount} required points. Please complete the polygon.`,
    };
  }

  // Check for out-of-bounds coordinates (percent space)
  const outOfBounds = points.some(
    (pt) => pt.x < 0 || pt.x > 100 || pt.y < 0 || pt.y > 100
  );
  if (outOfBounds) {
    return { valid: false, message: "Some polygon points are outside the valid drawing area." };
  }

  // Check for duplicate consecutive points
  for (let i = 0; i < points.length; i++) {
    const curr = points[i];
    const next = points[(i + 1) % points.length];
    if (Math.abs(curr.x - next.x) < 0.01 && Math.abs(curr.y - next.y) < 0.01) {
      return { valid: false, message: `Duplicate consecutive points detected at vertex ${i + 1}. Please adjust the polygon.` };
    }
  }

  return { valid: true, message: null };
};

/**
 * Format point count display label.
 * @param {number} current
 * @param {number} required
 * @returns {string}
 */
export const formatPointsLabel = (current, required) => {
  if (!required) return `${current} points`;
  return `${current} / ${required} points`;
};
